/**
 * ReplyPreviewDialog — "what will the customer actually receive?"
 *
 * Renders a reply through the tenant's default reply template server-side
 * (POST /conversations/:id/reply-preview, the same renderer the send path
 * uses) and shows the resulting subject line and email body. Used from two
 * places:
 *   - the reply composer, on a draft that has not been sent yet
 *   - the thread timeline, on a reply that already went out
 *
 * The rendered body is tenant-authored HTML, so it is shown inside a sandboxed
 * iframe (no scripts, no same-origin access) rather than injected into this
 * document.
 */
import { useEffect, useState, type JSX } from 'react'
import { Eye, FileText, Mail, X } from 'lucide-react'
import { api } from '../../../api/client.js'
import { R } from './primitives.js'

export interface ReplyPreviewDialogProps {
  open: boolean
  conversationId: string
  /** Draft body from the composer (rich text). Ignored when messageId is set. */
  contentHtml?: string | undefined
  /** Preview a reply already in the thread instead of a draft. */
  messageId?: string | undefined
  /** Subject typed in the composer (email drafts). Ignored when messageId is set. */
  emailSubject?: string | undefined
  onClose: () => void
}

interface PreviewResponse {
  subject: string
  html: string
  usingTemplate: boolean
  templateId: string | null
  templateName: string | null
  referenceId: string
}

/** Does this body already carry its own markup, or is it plain text? */
function looksLikeHtml(body: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(body)
}

/**
 * Wrap the rendered body in a minimal document for the preview frame.
 *
 * A templated reply is a full HTML email and is shown as-is. An untemplated one
 * is whatever the agent typed — plain text there would otherwise collapse into
 * one unstyled line, so it gets a readable shell instead.
 */
function previewDocument(html: string): string {
  const body = looksLikeHtml(html)
    ? html
    : `<pre style="margin:0;font:14px/1.6 -apple-system,Segoe UI,Roboto,Arial,sans-serif;white-space:pre-wrap;word-break:break-word">${html
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')}</pre>`
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>
    html,body{margin:0;padding:0;background:#fff}
    body{padding:16px;color:#0f172a;font:14px/1.6 -apple-system,Segoe UI,Roboto,Arial,sans-serif}
    img{max-width:100%;height:auto}
    table{max-width:100%}
  </style></head><body>${body}</body></html>`
}

export default function ReplyPreviewDialog({
  open,
  conversationId,
  contentHtml,
  messageId,
  emailSubject,
  onClose,
}: ReplyPreviewDialogProps): JSX.Element | null {
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setPreview(null)
    void (async () => {
      try {
        const res = await api.post<PreviewResponse>(`/conversations/${conversationId}/reply-preview`, {
          ...(messageId ? { messageId } : {}),
          ...(!messageId && contentHtml ? { contentHtml } : {}),
          ...(!messageId && emailSubject?.trim() ? { emailSubject: emailSubject.trim() } : {}),
        })
        if (!cancelled) setPreview(res)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Preview failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, conversationId, contentHtml, messageId, emailSubject])

  // Close on Escape, matching the drawer behaviour elsewhere in the detail view.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-[#0f172a]/40" onClick={onClose} />
      <div className="fixed inset-0 z-[71] flex items-center justify-center p-4">
        <div className={`flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden ${R} bg-white shadow-2xl`}>
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <div className="flex items-center gap-3">
              <span className={`flex h-8 w-8 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
                <Eye className="h-4 w-4" />
              </span>
              <div className="leading-tight">
                <h3 className="text-sm font-bold text-[#0f172a]">Reply preview</h3>
                <p className="text-[12px] text-slate-400">
                  {loading
                    ? 'Rendering…'
                    : preview?.usingTemplate
                      ? 'Rendered through the default reply template'
                      : preview
                        ? 'No default reply template — the reply goes out as written'
                        : 'How this reply reaches the customer'}
                </p>
              </div>
              {preview && (
                <span
                  className={`ml-1 inline-flex items-center gap-1.5 ${R} px-2 py-1 text-[11px] font-semibold ${
                    preview.usingTemplate
                      ? 'bg-blue-50 text-[#3366FF]'
                      : 'bg-amber-50 text-amber-700'
                  }`}
                  title={
                    preview.usingTemplate
                      ? 'The tenant’s default reply_template is applied to outbound replies'
                      : 'No active default reply template is configured for this tenant'
                  }
                >
                  <FileText className="h-3 w-3" />
                  {preview.usingTemplate
                    ? (preview.templateName ?? 'Reply template')
                    : 'No template'}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`flex h-8 w-8 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700`}
              aria-label="Close preview"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-1 flex-col overflow-hidden bg-[#F8FAFC] p-5">
            {loading && (
              <div className="flex flex-1 items-center justify-center gap-2 text-[13px] text-slate-400">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-[#3366FF]" />
                Building preview…
              </div>
            )}

            {!loading && error && (
              <div className={`${R} border border-red-100 bg-red-50 px-4 py-3 text-[13px] text-red-600`} role="alert">
                {error}
              </div>
            )}

            {!loading && !error && preview && (
              <div className={`flex flex-1 flex-col overflow-hidden ${R} border border-slate-200 bg-white`}>
                {/* Subject line — exactly what lands in the customer's inbox,
                    including the [TKT-…] reference the send path appends. */}
                <div className="flex items-start gap-2 border-b border-slate-100 px-4 py-3">
                  <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Subject</div>
                    <div className="break-words text-[13px] font-semibold text-[#0f172a]">{preview.subject}</div>
                  </div>
                </div>
                <iframe
                  title="Reply preview"
                  sandbox=""
                  srcDoc={previewDocument(preview.html)}
                  className="min-h-[320px] w-full flex-1 border-0 bg-white"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3">
            <span className="text-[11px] text-slate-400">
              {preview?.referenceId ? `Conversation ${preview.referenceId}` : ''}
            </span>
            <button
              type="button"
              onClick={onClose}
              className={`${R} bg-[#3366FF] px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC]`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
